import { keywords } from '../../parser/keywords.js';
import { Test, TestType, ObjectField } from '../../parser/models/all.js';
import { isClassDefinition } from "../../parser/utils/classUtils.js";
import { hasOnlyDecorator } from '../../parser/utils/testOnlyUtils.js';
import { hasTitleDecorator } from '../../parser/utils/testTitleUtils.js';
import { PreProcessContext } from '../models/preProcessContext.js';
import { getChildGroupName, getClassInstanceChildren } from './preProcessUtils.js';

/**
 * Processor for identifying and creating tests from class definitions.
 * It handles class decorators (only, title), default exports, and recurses into class members.
 * 
 * @param field - The object field containing the class definition.
 * @param index - Field index in the collection.
 * @param context - The pre-processing context.
 * @returns True if the field was processed as a class definition.
 */
export function processClassDef(field: ObjectField, index: number, context: PreProcessContext): boolean {
  const { name, value } = field;

  // class definition flags
  if (isClassDefinition(value)) {
    const isClassDefWithOnly = hasOnlyDecorator(field.value);
    const isClassDefWithTitle = hasTitleDecorator(field.value);
    const isDefaultExport = field.isDefaultExport;

    let type = TestType.CLASS_DEF;
    type |= isDefaultExport ? TestType.EXPORT_DEFAULT : 0;
    type |= isClassDefWithOnly ? TestType.CLASS_DEF_WITH_ONLY : 0;
    type |= isClassDefWithTitle ? TestType.CLASS_DEF_WITH_TITLE : 0;

    const nameOrTitle = isClassDefWithTitle
      ? value[keywords.title]
      : name;

    // avoid default entries
    if (isDefaultExport === false) {
      context.tests.push(
        new Test(
          field.groupName,
          nameOrTitle,
          value,
          new TestType(type),
          // test cases
          [],
          // only expression
          ""
        )
      );
    }

    // generate the child group name
    let childGroupName = isDefaultExport && isClassDefWithTitle === false
      ? field.groupName
      : getChildGroupName(field.groupName, nameOrTitle);

    // process new class instance
    const classInstance = new value();
    const testProps = getClassInstanceChildren(
      childGroupName,
      classInstance,
      context.options
    );

    context.tests.push(
      ...testProps
    );

    return true;
  }

  return false;
}
